// Maps rule types to their corresponding validation options.
import {getValidationRules} from "./helpers";

const ruleOptions = {
    'required': ['required'],
    'email': ['email'],
    'min': ['minLength'],
    'max': ['maxLength'],
    'password': ['password'],
    'numeric': ['number'],
    'integer': ['integer'],
    'digits': ['integer', 'minLength', 'maxLength'],
    'maxNumber': ['maxNumber'],
    'minNumber': ['minNumber'],
    'strongPassword': ['strongPassword'],
    'regex': ['customRegexp'],
    'minFilesCount': ['minFilesCount'],
    'maxFilesCount': ['maxFilesCount']
};

/**
 * Retrieves the value associated with a rule key.
 * @param key The rule key.
 * @param value The value for the rule.
 * @returns {*} The rule value.
 */
function ruleValue(key, value) {
    let rules = {
        'required': null,
        'email': null,
        'minLength': parseInt(value),
        'maxLength': parseInt(value),
        'password': null,
        'number': null,
        'integer': null,
        'maxNumber': parseInt(value),
        'minNumber': parseInt(value),
        'strongPassword': null,
        'customRegexp': value,
        'minFilesCount': parseInt(value),
        'maxFilesCount': parseInt(value)
    };

    return rules[key];
}

/**
 * Constructs a list of rules based on the provided key and value.
 * @param key The rule key.
 * @param value The value for the rule.
 * @returns {*[]} Array of rules with their values.
 */
export function getRules(key, value) {
    let rules = [];

    Helper.forEach(ruleOptions[key], (option, rule) => {
        let val = ruleValue(rule, value);

        if (val !== null) {
            rules.push({
                rule: rule, value: val
            });
        } else {
            rules.push({rule: rule});
        }
    });

    return rules;
}

let currentValidator = null;

/**
 * Initializes form validation for all forms with class `form-validate` in the document.
 * Destroys any existing validator instance before creating a new one for each form.
 * @param {Document} document - The document or container holding forms to validate.
 */
export function initializeValidation(document) {
    // Check if there is an existing validator and destroy it if necessary
    if (currentValidator) {
        currentValidator.destroy();
        currentValidator = null;
    }

    const forms = document.querySelectorAll('form.form-validate');
    forms.forEach(form => {
        const rules = getValidationRules(form);
        // Initialize a new validator instance
        currentValidator = createJustValidateInstance(form, rules);
        // Attach it to the global object if necessary
        window.ValidationForm = form;
        window.Validator = currentValidator;
    });
}

/**
 * Creates an instance of JustValidate with the specified element and rules.
 * @param element The form element to validate.
 * @param rules The validation rules to apply.
 * @returns {*} The JustValidate instance.
 */
export function createJustValidateInstance(element, rules) {
    const validator = new JustValidate(element, {
        validateBeforeSubmitting: true,
        errorLabelCssClass: 'validate-error-label',
        successLabelCssClass: 'validate-success-label',
        errorFieldCssClass: 'validate-error-field',
        successFieldCssClass: 'validate-success-field'
    }, JustValidateLocale.dictionary)
        .onSuccess(() => {
            element.classList.add('form-success');
            element.classList.remove('form-fail');
        })
        .onFail(() => {
            element.classList.remove('form-success');
            element.classList.add('form-fail');
        });

    validator.setCurrentLocale(APP_LANG_LOCALE);

    rules = rules.filter(ruleInfo => !isFieldDisabled(ruleInfo.field));

    return addValidatorFields(validator, rules);
}

/**
 * Checks if a field is disabled.
 * @param fieldSelector The selector for the field to check.
 * @returns {boolean} True if the field is disabled, false otherwise.
 */
function isFieldDisabled(fieldSelector) {
    let field = document.querySelector(fieldSelector);
    return field && field.disabled;
}

/**
 * Modifies validation rules based on the provided data.
 * @param data The original rules data.
 * @returns {*[]} Array of modified validation rules.
 */
export function modifyAllRules(data) {
    let modifiedRules = [];

    Helper.forEach(data, (fieldName, rules) => {
        Helper.forEach(document.querySelectorAll(`[data-modified-name="${fieldName}"]`), function (key, field) {
            let name = field.getAttribute('name'),
                modifiedFieldRules = {
                    field: `[name="${name}"]`,
                    rules: [],
                    globalConfig: {
                        errorsContainer: document.querySelector(`.validate-error-container[id="error-container-${name}"]`)
                    }
                };

            modifiedRules.push(modifyFieldRules(modifiedFieldRules, rules));
        });
    });

    return modifiedRules;
}

/**
 * Modifies field rules based on the original rules.
 * @param modifiedFieldRules The field rules to modify.
 * @param rules The original rules.
 * @returns {*} The modified field rules.
 */
function modifyFieldRules(modifiedFieldRules, rules) {
    Helper.forEach(rules, (index, rule) => {
        let [key, value] = rule.includes(':') ? (rule.includes('regex:') ? ['regex', new RegExp(rule.split('regex:')[1].slice(1, -1))] : rule.split(':')) : [rule, null];

        if ((Object.values(rules).includes('numeric') || Object.values(rules).includes('integer')) && (rule.includes('min') || rule.includes('max'))) {
            key = `${key}Number`;
        }

        if (ruleOptions[key] !== undefined) {
            modifiedFieldRules.rules.push(...getRules(key, value));
        }
    });

    /**
     * If the rules state the field is "required", it's an "array", and a minimum count is set,
     * apply the 'minFilesCount' rule to ensure at least one file is provided.
     */
    if (rules.includes('required') && rules.includes('array') && rules.some(rule => rule.startsWith('min'))) {
        modifiedFieldRules.rules.push(...getRules('minFilesCount', 1)); //1 is the min number of files
    }

    return modifiedFieldRules;
}

/**
 * Adds validator fields to the JustValidate instance based on the rules.
 * @param validator The JustValidate instance.
 * @param rules The validation rules to apply.
 * @returns {*} The updated JustValidate instance.
 */
function addValidatorFields(validator, rules) {
    Helper.forEach(rules, (key, item) => {
        if (item.field === '[name="services[]"]') {
            validator = validator.addRequiredGroup('.form-control-group');
        } else {
            Helper.forEach(document.querySelectorAll(item.field), (key, field) => {
                if(!field.disabled && item.rules.length > 0){
                    validator = validator.addField(field, item.rules, item.globalConfig);
                }
                field.addEventListener('blur', (field) => {
                    Validator.revalidateField(field.target).then(() => {
                        document.querySelector(`#error-container-${field.target.name} .backend-error-label`)
                            ?.remove()
                    });
                });
            });
        }
    });

    return validator;
}

/**
 * Refreshes the validation fields with new rules.
 * @param rules The new validation rules.
 */
export function refreshValidatorFields(rules) {
    if (!window.Validator) return;
    Helper.forEach(window.Validator.fields, function (item) {
        let field = window.Validator.fields[item].elem;
        window.Validator.removeField(field);

        removeEventListener('blur', field);
    });

    rules = rules.filter(ruleInfo => !isFieldDisabled(ruleInfo.field));
    window.Validator = createJustValidateInstance(window.ValidationForm, rules);

    addValidatorFields(window.Validator, rules);
    window.Validator.revalidate().then(() => {});
}
